import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { openSidebar } from './sidebarSlice';
import { openModal } from './modalSlice';

// Async thunk for executing actions
export const executeAction = createAsyncThunk(
  'actions/executeAction',
  async (
    { actionType, section, data, additionalParams = {}, t },
    { dispatch }
  ) => {
    try {
      let response;

      switch (actionType) {
        case 'save':
          response = await handleSaveAction(section, data, t, additionalParams);
          break;
        case 'edit':
          response = await handleEditAction(
            section,
            data,
            t,
            additionalParams,
            dispatch
          );
          break;
        case 'delete':
          response = await handleDeleteAction(
            section,
            data,
            t,
            additionalParams,
            dispatch
          );
          break;
        case 'print':
          response = await handlePrintAction(
            section,
            data,
            t,
            additionalParams,
            dispatch
          );
          break;
        case 'email':
          response = await handleEmailAction(
            section,
            data,
            t,
            additionalParams
          );
          break;
        case 'add':
          response = await handleAddAction(
            section,
            data,
            t,
            additionalParams,
            dispatch
          );
          break;
        default:
          throw new Error(`Unknown action type: ${actionType}`);
      }

      return { success: true, data: response, actionType, section };
    } catch (error) {
      console.error(
        `Error executing ${actionType} action for ${section}:`,
        error
      );
      throw error;
    }
  }
);

// Action handlers for different sections
const handleSaveAction = async (section, data, t, additionalParams) => {
  switch (section) {
    case 'businessProfile':
      return await nodeAxiosFirebase({
        t,
        method: 'PATCH',
        url: `main/general`,
        body: {
          lang: additionalParams.lang || 'fr',
          businessData: { ...data },
        },
      });

    case 'mainBoard':
      return await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: `aura/manage`,
        body: {
          auraId: data?.id,
          businessId: null,
          type: 'general',
          data: data,
          lang: additionalParams.lang || 'fr',
          structureId: additionalParams.structureId,
        },
      });

    case 'maestroConfig':
      return await nodeAxiosFirebase({
        t,
        method: 'PATCH',
        url: `maestro/general`,
        body: {
          lang: additionalParams.lang || 'fr',
          data: data,
        },
      });

    default:
      throw new Error(`Unknown section for save action: ${section}`);
  }
};

const handleEditAction = async (
  section,
  data,
  t,
  additionalParams,
  dispatch
) => {
  switch (section) {
    case 'element':
      const { openSidebar } = await import('./sidebarSlice');
      dispatch(
        openSidebar({
          sidebarType: 'edit',
          sidebarProps: {
            moduleName: additionalParams.moduleName,
            elementId: additionalParams.elementId,
            structureId: additionalParams.structureId,
            elementPath: additionalParams.elementPath,
          },
        })
      );
      return { action: 'sidebarOpened', type: 'edit' };
    default:
      throw new Error(`Unknown section for edit action: ${section}`);
  }
};

const handleDeleteAction = async (
  section,
  data,
  t,
  additionalParams,
  dispatch
) => {
  switch (section) {
    case 'element':
      dispatch(
        openModal({
          modalType: 'confirmDelete',
          modalProps: {
            type: additionalParams.type,
            elementId: additionalParams.elementId,
            withoutConfirmation: additionalParams.withoutConfirmation,
            data: data,
            onConfirm: additionalParams.onConfirm,
          },
        })
      );
      return { action: 'modalOpened', type: 'confirmDelete' };
    case 'docs':
      // Docs section doesn't support delete action
      console.warn('Delete action is not supported for docs section');
      return { action: 'noOp', section: 'docs' };
    default:
      throw new Error(`Unknown section for delete action: ${section}`);
  }
};

const handlePrintAction = async (
  section,
  data,
  t,
  additionalParams,
  dispatch
) => {
  switch (section) {
    case 'element':
      dispatch(
        openModal({
          modalType: 'print',
          modalProps: {
            elementData: data,
            structureId: additionalParams.structureId,
            moduleName: additionalParams.moduleName,
            elementId: additionalParams.elementId,
          },
        })
      );
      return { action: 'modalOpened', type: 'print' };
    default:
      throw new Error(`Unknown section for print action: ${section}`);
  }
};

const handleEmailAction = async (section, data, t, additionalParams) => {
  switch (section) {
    case 'element':
      return null;
    default:
      throw new Error(`Unknown section for email action: ${section}`);
  }
};

const handleAddAction = async (
  section,
  data,
  t,
  additionalParams,
  dispatch
) => {
  switch (section) {
    case 'automations':
      dispatch(
        openModal({
          modalType: 'automationBuilder',
          modalProps: { mode: 'create' },
        })
      );
      return { action: 'modalOpened', type: 'automationBuilder' };
    case 'accounts':
      dispatch(
        openModal({
          modalType: 'accountCreation',
          modalProps: {},
        })
      );
      return { action: 'modalOpened', type: 'accountCreatione' };
    case 'mainBoard':
      dispatch(
        openSidebar({
          sidebarType: 'addBlock',
          sidebarProps: {},
        })
      );
      return { action: 'modalOpened', type: 'accountCreation' };
    case 'maestroAccounts':
      dispatch(
        openModal({
          modalType: 'createMaestroAccount',
          modalProps: {},
        })
      );
      return { action: 'modalOpened', type: 'createMaestroAccount' };
    case 'maestroModels':
      dispatch(
        openModal({
          modalType: 'createMaestroModel',
          modalProps: {},
        })
      );
      return { action: 'modalOpened', type: 'createMaestroModel' };
    default:
      throw new Error(`Unknown section for add action: ${section}`);
  }
};

// Slice definition
const actionsSlice = createSlice({
  name: 'actions',
  initialState: {
    currentSection: null,
    currentData: null,
    additionalParams: {},
    loading: false,
    error: null,
    lastAction: null,
    docsSearchQuery: '',
  },
  reducers: {
    setCurrentSection: (state, action) => {
      state.currentSection = action.payload.section;
      state.currentData = action.payload.data || null;
      state.additionalParams = action.payload.additionalParams || {};
    },
    clearCurrentSection: (state) => {
      state.currentSection = null;
      state.currentData = null;
      state.additionalParams = {};
    },
    setAdditionalParams: (state, action) => {
      state.additionalParams = { ...state.additionalParams, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    setDocsSearchQueryLocal: (state, action) => {
      state.docsSearchQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(executeAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(executeAction.fulfilled, (state, action) => {
        state.loading = false;
        state.lastAction = {
          type: action.payload.actionType,
          section: action.payload.section,
          timestamp: new Date().toISOString(),
          success: true,
        };
      })
      .addCase(executeAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.lastAction = {
          type: action.meta.arg.actionType,
          section: action.meta.arg.section,
          timestamp: new Date().toISOString(),
          success: false,
          error: action.error.message,
        };
      });
  },
});

// Export actions
export const {
  setCurrentSection,
  clearCurrentSection,
  setAdditionalParams,
  clearError,
  setDocsSearchQueryLocal,
} = actionsSlice.actions;

// Action for Docs search
export const setDocsSearchQuery = createAsyncThunk(
  'actions/setDocsSearchQuery',
  async (searchQuery, { dispatch }) => {
    // This will be handled by the Docs component
    return { searchQuery };
  }
);

// Export selectors
export const selectCurrentSection = (state) => state.actions.currentSection;
export const selectCurrentData = (state) => state.actions.currentData;
export const selectAdditionalParams = (state) => state.actions.additionalParams;
export const selectActionsLoading = (state) => state.actions.loading;
export const selectActionsError = (state) => state.actions.error;
export const selectLastAction = (state) => state.actions.lastAction;
export const selectDocsSearchQuery = (state) => state.actions.docsSearchQuery;

export default actionsSlice.reducer;
